/**
 * Определение и сохранение выбранного языка интерфейса.
 *
 * Модуль намеренно не импортирует ничего из проекта. Его читает и
 * `src/utils/i18n.ts` — ради стартового значения, и `src/stores/language.ts` —
 * ради сохранения выбора. Если бы определение жило в сторе, а стор
 * импортировал i18next, вышел бы круговой импорт `i18n` -> стор -> `i18n`,
 * при котором один из участников на момент инициализации другого ещё
 * не определён.
 */

export const SUPPORTED_LANGUAGES = ['ru', 'en'] as const;

export type Language = (typeof SUPPORTED_LANGUAGES)[number];

/** Ключ, под которым выбор языка живёт в localStorage */
const STORAGE_KEY = 'language';

export function isLanguage(value: unknown): value is Language {
	return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/**
 * Сохранённый выбор, либо null. В приватном окне обращение к localStorage
 * бросает исключение — интерфейс на этом падать не должен, поэтому
 * недоступное хранилище равносильно отсутствию выбора.
 */
function readStoredLanguage(): Language | null {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return isLanguage(stored) ? stored : null;
	} catch {
		return null;
	}
}

/** Язык браузера, приведённый к поддерживаемому: и `ru`, и `ru-RU` дают `ru` */
function detectBrowserLanguage(): Language {
	const tag = typeof navigator === 'undefined' ? '' : navigator.language;
	return tag.toLowerCase().startsWith('ru') ? 'ru' : 'en';
}

/** Сохранённый выбор, а при его отсутствии — язык браузера */
export function resolveInitialLanguage(): Language {
	return readStoredLanguage() ?? detectBrowserLanguage();
}

/** Запоминает выбор до следующего явного изменения */
export function persistLanguage(language: Language): void {
	try {
		localStorage.setItem(STORAGE_KEY, language);
	} catch {
		/* Хранилище недоступно — выбор живёт до перезагрузки страницы */
	}
}
